import { getTableTypicalColumn } from "utils/components";
import { cn } from "utils/styling";

import type { HeatmapCell, HeatmapCellValue, HeatmapRange } from "./types";

import { formatHeatmapValue, getHeatmapCellClass, getHeatmapInteractionCellClass } from "./heatmapScale";

type BuildHeatmapTableHeadersParams = {
  columnLabels: string[];
  formatValue?: (value: HeatmapCellValue) => string;
  getInteractionCellClass?: () => string;
  isCellHighlighted: (cell: HeatmapCell) => boolean;
  isCellHovered: (cell: HeatmapCell) => boolean;
  onCellClick: (cell: HeatmapCell) => void;
  onCellEnter: (cell: HeatmapCell) => void;
  range: HeatmapRange;
  rowHeaderLabel: string;
};

type HeatmapRow = Record<string, unknown> & {
  rowIndex: number;
};

const getCellValue = (row: HeatmapRow, columnIndex: number): HeatmapCellValue => {
  const value = row[`c${columnIndex}`];
  return typeof value === "number" ? value : null;
};

export const buildHeatmapTableData = (values: HeatmapCellValue[][], rowLabels: string[]) =>
  values.map((row, rowIndex) => ({
    id: rowIndex,
    label: rowLabels[rowIndex],
    rowIndex,
    ...Object.fromEntries(row.map((value, columnIndex) => [`c${columnIndex}`, value])),
  }));

export const buildHeatmapTableHeaders = ({
  columnLabels,
  formatValue = formatHeatmapValue,
  getInteractionCellClass = getHeatmapInteractionCellClass,
  isCellHighlighted,
  isCellHovered,
  onCellClick,
  onCellEnter,
  range,
  rowHeaderLabel,
}: BuildHeatmapTableHeadersParams) => [
  getTableTypicalColumn({
    dataKey: "label",
    disableSort: true,
    isReadOnly: true,
    label: rowHeaderLabel,
    valueClassName: "text-lds-foreground-secondary font-ds-body-regular-xxs",
  }),
  ...columnLabels.map((label, columnIndex) =>
    getTableTypicalColumn({
      cellType: "custom",
      dataKey: `c${columnIndex}`,
      disableSort: true,
      isReadOnly: true,
      label,
      onCellClick: (row: HeatmapRow) => onCellClick({ columnIndex, rowIndex: row.rowIndex }),
      onCellEnter: (row: HeatmapRow) => onCellEnter({ columnIndex, rowIndex: row.rowIndex }),
      valueClassName: (row: HeatmapRow) => {
        const cell = { columnIndex, rowIndex: row.rowIndex };
        const isInteracting = isCellHighlighted(cell) || isCellHovered(cell);

        return cn(
          "font-ds-body-regular-xxs",
          isInteracting ? getInteractionCellClass() : getHeatmapCellClass(getCellValue(row, columnIndex), range)
        );
      },
      valueTransformation: (row: HeatmapRow) => formatValue(getCellValue(row, columnIndex)),
    })
  ),
];
